import { QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Tooltip } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { AdminShell } from '@/components/admin-shell';
import { SessionIdleGuard } from '@/components/session-idle-guard';
import { Toaster } from '@/components/ui/toast';
import { RequireAuth, RequirePlatformManager } from '@/lib/guards';
import { queryClient } from '@/lib/query-client';
import { AppVersionPage } from '@/pages/app-version-page';
import { AuditPage } from '@/pages/audit-page';
import { ChurchesPage } from '@/pages/churches-page';
import { ConsoleSessionPage } from '@/pages/console-session-page';
import { CouponsPage } from '@/pages/coupons-page';
import { DashboardPage } from '@/pages/dashboard-page';
import { DefaultImagesPage } from '@/pages/default-images-page';
import { GivingPaymentsPage } from '@/pages/giving-payments-page';
import { EmailPage } from '@/pages/email-page';
import { SmsPage } from '@/pages/sms-page';
import { InvoicesPage } from '@/pages/invoices-page';
import { OperationsPage } from '@/pages/operations-page';
import { ReceiptsPage } from '@/pages/receipts-page';
import { SignInPage } from '@/pages/sign-in-page';
import { SupportAccessPage } from '@/pages/support-access-page';
import { SupportTicketsPage } from '@/pages/support-tickets-page';
import { UsersPage } from '@/pages/users-page';

const AdminPage = ({ children }: { children: ReactNode }) => (
  <RequireAuth>
    <RequirePlatformManager>
      <AdminShell>{children}</AdminShell>
    </RequirePlatformManager>
  </RequireAuth>
);

const ToasterWithLabels = () => {
  const { t } = useTranslation('common');
  return <Toaster closeLabel={t('action.close')} />;
};

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <Tooltip.Provider delayDuration={300}>
      <SessionIdleGuard />
      <BrowserRouter>
        <Routes>
          <Route path="/sign-in" element={<SignInPage />} />
          <Route
            path="/"
            element={
              <AdminPage>
                <DashboardPage />
              </AdminPage>
            }
          />
          <Route
            path="/churches"
            element={
              <AdminPage>
                <ChurchesPage />
              </AdminPage>
            }
          />
          <Route
            path="/users"
            element={
              <AdminPage>
                <UsersPage />
              </AdminPage>
            }
          />
          <Route
            path="/audit"
            element={
              <AdminPage>
                <AuditPage />
              </AdminPage>
            }
          />
          <Route
            path="/support-access"
            element={
              <AdminPage>
                <SupportAccessPage />
              </AdminPage>
            }
          />
          <Route
            path="/support/tickets"
            element={
              <AdminPage>
                <SupportTicketsPage />
              </AdminPage>
            }
          />
          <Route
            path="/giving-payments"
            element={
              <AdminPage>
                <GivingPaymentsPage />
              </AdminPage>
            }
          />
          <Route
            path="/email"
            element={
              <AdminPage>
                <EmailPage />
              </AdminPage>
            }
          />
          <Route
            path="/sms"
            element={
              <AdminPage>
                <SmsPage />
              </AdminPage>
            }
          />
          <Route
            path="/sms/credits"
            element={
              <AdminPage>
                <SmsPage />
              </AdminPage>
            }
          />
          <Route
            path="/coupons"
            element={
              <AdminPage>
                <CouponsPage />
              </AdminPage>
            }
          />
          <Route
            path="/invoices"
            element={
              <AdminPage>
                <InvoicesPage />
              </AdminPage>
            }
          />
          <Route
            path="/receipts"
            element={
              <AdminPage>
                <ReceiptsPage />
              </AdminPage>
            }
          />
          <Route
            path="/default-images"
            element={
              <AdminPage>
                <DefaultImagesPage />
              </AdminPage>
            }
          />
          <Route
            path="/app-version"
            element={
              <AdminPage>
                <AppVersionPage />
              </AdminPage>
            }
          />
          <Route
            path="/console-session"
            element={
              <AdminPage>
                <ConsoleSessionPage />
              </AdminPage>
            }
          />
          <Route
            path="/operations"
            element={
              <AdminPage>
                <OperationsPage />
              </AdminPage>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <ToasterWithLabels />
    </Tooltip.Provider>
  </QueryClientProvider>
);
